import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { PasswordResetRequest } from "../components/auth/PasswordResetRequest";
import { EmailConfirmation } from "../components/auth/EmailConfirmation";
import { CreateNewPassword } from "../components/auth/CreateNewPassword";
import { PasswordResetSuccess } from "../components/auth/PasswordResetSuccess";
import { AuthService } from "../services/auth.service.real";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { HttpError } from "../services/http.service";

type ResetStep = "request" | "email-sent" | "new-password" | "success";

export const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<ResetStep>("request");
  const [resetToken, setResetToken] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    // Check if token is in URL (from email link)
    const token = searchParams.get("token");
    if (token) {
      setResetToken(token);
      setStep("new-password");
    }
  }, [searchParams]);

  const requestResetMutation = useMutation({
    mutationFn: (email: string) => AuthService.requestPasswordReset(email),
    onSuccess: () => {
      // Clear any previous errors
      setError("");
      // Show email confirmation view
      setStep("email-sent");
    },
    onError: (err) => {
      console.error("Password reset request failed:", err);
      if (err instanceof HttpError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred while requesting password reset");
      }
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      AuthService.resetPassword(token, password),
    onSuccess: () => {
      // Clear any previous errors
      setError("");
      setStep("success");
    },
    onError: (err) => {
      console.error("Password reset failed:", err);
      if (err instanceof HttpError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred while resetting password");
      }
    },
  });

  const handleRequestReset = async (data: { email: string }) => {
    setResetEmail(data.email);
    await requestResetMutation.mutateAsync(data.email);
  };

  const handleResetPassword = async (data: { password: string; confirmPassword: string }) => {
    if (!resetToken) {
      const token = prompt("Enter reset token from email:") || "";
      if (!token) return;
      setResetToken(token);
    }
    await resetPasswordMutation.mutateAsync({ token: resetToken, password: data.password });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-arial bg-primary-blue">
      <div>
        {error && (
          <ErrorMessage message={error} className="mb-4 max-w-sm" onDismiss={() => setError("")} />
        )}

        {step === "request" && (
          <PasswordResetRequest
            onSubmit={handleRequestReset}
            isLoading={requestResetMutation.isPending}
          />
        )}

        {step === "email-sent" && <EmailConfirmation email={resetEmail} />}

        {step === "new-password" && (
          <CreateNewPassword
            onSubmit={handleResetPassword}
            isLoading={resetPasswordMutation.isPending}
          />
        )}

        {step === "success" && <PasswordResetSuccess />}
      </div>
    </div>
  );
};
